import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import GeneralButton from "../common/GeneralButton";
import { TiChevronLeft, TiChevronRight, TiPlus } from "react-icons/ti";
import {
  useStep,
  useAddress,
  useKid,
  useChildDetails,
} from "../contexts/SubscriptionContext";
import Icon from "../common/Icon";
import CMSFormInput from "../formFields/CMSFormInput";
import CMSFormTelephone from "../formFields/CMSFormTelephone";
import { Form, Formik } from "formik";
import { addressSchema, childSchema } from "../utils/validations";
import { useToast } from "../contexts/ToastContext";
import Modal from "../common/Modal";
import CMSFormSelect from "../formFields/CMSFormSelect";
import { useCart } from "../contexts/CartContext";
import { useNavigate, useParams } from "react-router-dom";
import ContainerLoading from "../common/loading/ContainerLoading";
import CustomAlert from "../common/CustomAlert";
import { useGeneral } from "../contexts/GeneralContext";

const MealChildManagement = () => {
  const { type } = useParams();
  const { nextStep, prevStep } = useStep();
  const navigate = useNavigate();
  const { extraContent } = useGeneral();
  const {
    savedKids,
    selectedKidId,
    setSelectedKidId,
    loading,
    fetchKids,
    addKid,
    updateKid,
    selectKid,
  } = useKid();
  const { cart } = useCart();
  const [state, setState] = useState(savedKids.length > 0 ? "list" : "add");
  const [deletingAddress, setDeletingAddress] = useState(null);
  let user = localStorage.getItem("loggedInUser");
  const handleCloseModal = () => setDeletingAddress(null);
  if (user && user !== undefined && user !== "undefined") {
    user = JSON.parse(user);
  }
  const { childDetails, updateChildDetails } = useChildDetails();
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [editingAddress, setEditingAddress] = useState(null);
  const { addToast } = useToast();
  // If editing, populate form with existing data
  useEffect(() => {
    if (editingAddress) setState("add");
  }, [editingAddress]);
  const [hasFetched, setHasFetched] = useState(false);

  useEffect(() => {
    if (!hasFetched) {
      fetchKids();
      setHasFetched(true);
    }
  }, [hasFetched]);
  useEffect(() => {
    setState(savedKids.length > 0 ? "list" : "add");
  }, [savedKids]);
  const handleSubmit = async (values, resetForm) => {
    try {
      let success = false;

      if (editingAddress) {
        success = await updateKid(editingAddress._id, values);

        if (success) {
          addToast("Child details updated successfully", "success");
          setEditingAddress(null);
        }
      } else {
        success = await addKid(values);
        if (success) {
          addToast("Child added successfully", "success");
        }
      }

      if (success) {
        resetForm();
        setState("list");
      } else {
        setError("Failed to save child. Please try again.");
        setTimeout(() => {
          setError("");
        }, 5000);
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      setError("Failed to save child. Please try again.");
      setTimeout(() => {
        setError("");
      }, 5000);
    }
  };
  const refs = {
    firstName: useRef(null),
    lastName: useRef(null),
    age: useRef(null),
    gender: useRef(null),
    school: useRef(null),
  };

  const scrollToFirstError = (errors) => {
    const firstErrorField = Object.keys(errors)[0];
    if (!firstErrorField || !refs[firstErrorField]?.current) return;

    const el = refs[firstErrorField].current;

    // Smoothly scroll the nearest scrollable parent
    el.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "nearest",
    });

    // Give the scroll some time to finish, then focus
    setTimeout(() => {
      el.focus?.({ preventScroll: true });
    }, 500); // adjust based on your animation speed
  };
  const hasAutoSelected = useRef(false);
  useEffect(() => {
    const autoSelect = async () => {
      if (hasAutoSelected.current) return; // ✅ Prevent repeat
      const id = selectedKidId;
      if (!id || !savedKids?.length) return;

      const addr = savedKids.find((a) => a._id === id);
      if (!addr) return;

      try {
        let success = false;

        const res = await selectKid(addr._id);
        success = res?.success || false;

        if (success) {
          hasAutoSelected.current = true;
        } else {
         // console.error("Auto-select API failed");
        }
      } catch (err) {
        //console.error("Auto-select failed:", err);
      }
    };
    if (cart?.customer) {
      autoSelect();
    }
  }, [savedKids, selectedKidId]);
  return (
    <motion.div
      initial={{ x: -200, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: -200, opacity: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      <div className="grid grid-cols-1 lg:grid-cols-1 2xl:grid-cols-[1.19fr_1fr] gap-8 items-start ">
        <div>
          <p className="text-34pt pb-4 lg:pb-9">Child details</p>
          <div className="bg-[#DEE2DD]/40 w-full relative rounded-[15px] p-5 md:px-10 md:py-10 2.5xl:px-24 2.5xl:py-[70px] relative">
            {state === "list" ? (
              <p className="text-green text-21pt">Saved Children</p>
            ) : editingAddress != null ? (
              <p className="text-green text-21pt">Edit Child Details</p>
            ) : (
              <p className="text-green text-21pt">Add Child Details</p>
            )}
            {loading && (
              <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-20 rounded-lg">
                <ContainerLoading />
              </div>
            )}
            {state === "list" && (
              <div className="grid grid-cols-1 gap-8 mt-8">
                
                {savedKids?.map((child, index) => {
                  const id = selectedKidId;
                  const isSelected = id === child._id;

                  return (
                    <div
                      key={index}
                      className="flex flex-col gap-6 bg-[#fff] rounded-[15px] py-4 px-6 relative"
                    >
                      <div
                        className={`flex flex-row items-center justify-between`}
                      
                      >
                        <div className="flex items-center w-full"   onClick={() => {
                          selectKid(child._id);
                          localStorage.setItem(
                            "selectedChild",
                            JSON.stringify(child)
                          );
                          updateChildDetails(child);
                        }}>
                          <div className="flex items-center justify-start mb-3 md:mb-2 me-4 md:me-6">
                            <Icon
                              icon={isSelected ? "radioSelected" : "radio"}
                              className="w-[27px] h-[32px]"
                            />
                          </div>
                          <div className="w-[76px] h-[46px] -mt-1 md:w-[106px] md:h-[76px] bg-white border border-[#DEE2DD] rounded-full flex items-center justify-center me-4 md:me-6 lg:me-6 relative">
                            <Icon
                              icon="profile"
                              className={`w-[27px] h-[40px] md:w-[47px] md:h-[60px] ${
                                child.gender?.toLowerCase() === "girl"
                                  ? "text-[#FC05B0]/40"
                                  : "text-[#0B81FD]/40"
                              }`}
                            />
                          </div>

                          <div className="w-full">
                            <p className="text-18pt md:text-24pt leading-tight text-left">
                              {child.firstName} {child.lastName}
                            </p>
                            <p className="text-16pt mt-1 leading-tight text-left font-mono">
                              {child.gender} - {child.age} years old
                            </p>
                            <p className="text-12pt leading-tight text-left mt-1 text-orange">
                              {child.schoolName}
                            </p>
                          </div>
                        </div>
                        <div
                          title="Click to edit child details"
                          className="cursor-pointer "
                          onClick={() => setEditingAddress(child)}
                        >
                          <Icon
                            icon="edit"
                            className={`w-[27px] text-green hover:text-secondary`}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}

                <GeneralButton
                  onClick={() => {
                    setEditingAddress(null);
                    setState("add");
                  }}
                  text="Add New Child"
                  title="Click to add new child"
                  bgColor="#fff"
                  textColor="#295B24"
                  icon={<TiPlus size={24} className="ms-1" />}
                  className="text-24pt hover:text-secondary brown"
                  hover="brown"
                />
              </div>
            )}

            {state === "add" && (
              <div className="grid grid-cols-1 gap-12 mt-8">
                <Formik
                  enableReinitialize
                  initialValues={{
                    firstName: editingAddress?.firstName || "",
                    lastName: editingAddress?.lastName || "",
                    age: editingAddress?.age || "",
                    gender: editingAddress?.gender || "",
                    schoolName: editingAddress?.schoolName || "",
                  }}
                  validationSchema={childSchema}
                  onSubmit={(values) => {
                    handleSubmit(values);
                    //updateChildDetails(values); // save to context
                    //nextStep();
                    //saveToLocal();
                    //navigate("/subscribe/disclaimer");
                  }}
                >
                  {(form) => (
                    <div className="">
                      <div>
                        {/* Gray background box */}
                        <div className=" relative flex items-center justify-center">
                          <div className="w-full">
                            <Form className="w-full  grid grid-cols-1 gap-8">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                                <CMSFormInput
                                  name="firstName"
                                  label="First Name"
                                  ref={refs.firstName}
                                  form={form}
                                  required
                                />
                                <CMSFormInput
                                  name="lastName"
                                  label="Last Name"
                                  ref={refs.lastName}
                                  form={form}
                                  required
                                />
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                                <CMSFormInput
                                  name="age"
                                  label="Age"
                                  form={form}
                                  ref={refs.age}
                                  required
                                />
                                <CMSFormSelect
                                  name="gender"
                                  label="Gender"
                                  onChange={form.handleChange}
                                  selected={form.values["gender"]}
                                  placeholder={{
                                    label: "Select Gender",
                                    value: "",
                                  }}
                                  form={form}
                                  ref={refs.gender}
                                  required
                                  options={[
                                    { label: "Girl", value: "Girl" },
                                    { label: "Boy", value: "Boy" },
                                  ]}
                                />
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                                <CMSFormInput
                                  name="schoolName"
                                  ref={refs.school}
                                  label="School"
                                  form={form}
                                />
                              </div>
                              {/* {form.values.age && form.values.age > 12 && (
                                <CustomAlert
                                  type="warning"
                                  message={
                                    extraContent?.mealSubscriptionMessages
                                      ?.maxAge ||
                                    `Please note: The meal portion is specifically designed to meet the dietary needs of a 12-year-old child. Since your child is ${form.values.age} years old, this portion may be smaller than what they typically require.`
                                  }
                                />
                              )} */}
                              {form.values.age && form.values.age < 2 && (
                                <CustomAlert
                                  type="warning"
                                  message={
                                    extraContent?.mealSubscriptionMessages
                                      ?.minAge ||
                                    `Please note: These meals are designed for children aged 2–12 years. Since your child is under 2, the portion may not be the ideal fit.`
                                  }
                                />
                              )}
                            </Form>
                          </div>
                        </div>
{error && (
                        <div
                          className={`relative h-[20px] transition-all duration-300 ease-in-out my-3 ${
                            error
                              ? "opacity-100 translate-y-0"
                              : "opacity-0 -translate-y-2"
                          }`}
                        >
                          {error && (
                            <span className="errorMessage">{error}</span>
                          )}
                        </div>
                      )}

                        {/* Prev/Next buttons outside the gray box */}
                        <div className="flex flex-col-reverse md:flex-row items-center justify-between mt-10 gap-5">
                          {savedKids.length > 0 ? (
                            <GeneralButton
                              onClick={() => {
                                setEditingAddress(null);
                                setState("list");
                              }}
                              text="Saved Children"
                              title="Click to go back to child listing"
                              bgColor="#fff"
                              iconPosition="Left"
                              textColor="#295B24"
                              icon={<TiChevronLeft size={24} />}
                              className="text-24pt hover:text-secondary brown"
                              hover="brown"
                            />
                          ) : (
                            <div></div>
                          )}
                          <GeneralButton
                            onClick={() => {
                              form.handleSubmit();
                              setTimeout(
                                () => scrollToFirstError(form?.errors),
                                100
                              );
                            }}
                            text={editingAddress ? "Update Child" : "Add Child"}
                            title="Click to add child"
                            type={"submit"}
                            iconPosition="Right"
                            textColor="#fff"
                            icon={<TiPlus size={24} />}
                            className="text-24pt"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </Formik>
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="flex flex-col xs:flex-row gap-5 items-center justify-between mt-8 lg:mt-10">
            <GeneralButton
              onClick={() => {
                navigate(-1);
              }}
              text="Prev Step"
              title=""
              iconPosition="Left"
              bgColor="#ccc"
              textColor="#000"
              icon={<TiChevronLeft size={24} />}
              className="text-24pt"
            />
            <GeneralButton
              onClick={() => {
                if (selectedKidId) {
                  nextStep();
                  navigate("/subscribe/date");
                } else {
                  setShow(true);
                }
              }}
              text="Next Step"
              title=""
              bgColor="#30160E"
              textColor="#ccc"
              icon={<TiChevronRight size={24} />}
              className="text-24pt"
            />
          </div>
        </div>
      </div>
      <Modal
        isOpen={show}
        onClose={() => setShow(false)}
        title="Select a child"
        onConfirm={() => setShow(false)}
        confirmButton="Ok"
        dismissible={false}
        showCancel={false}
      >
        <div className="font-mono">
          <p>Please select a child or add a new child details to continue.</p>
        </div>
      </Modal>
    </motion.div>
  );
};

export default MealChildManagement;
